import { Injectable } from '@angular/core'
import {
  IWsMessage,
  IWsMessageAddTaskSubscriber,
  IWsMessageAddTaskTag,
  IWsMessageChangeGroupTaskSubtask,
  IWsMessageCreateTask,
  IWsMessageCreateTaskSubtask,
  IWsMessageCreateTaskSubtaskGroup,
  IWsMessageCreateTaskTextBlock,
  IWsMessageDeleteTask,
  IWsMessageDeleteTaskSubscriber,
  IWsMessageDeleteTaskSubtask,
  IWsMessageDeleteTaskSubtaskGroup,
  IWsMessageDeleteTaskTag,
  IWsMessageDeleteTaskTextBlock,
  IWsMessageRenameTaskSubtaskGroup,
  IWsMessageReplaceTaskSubscriber,
  IWsMessageDeleteProject,
  IWsMessageUpdateProject,
  IWsMessageUpdateStream,
  IWsMessageUpdateTaskDuration,
  IWsMessageUpdateTaskOwner,
  IWsMessageUpdateTaskPoint,
  IWsMessageUpdateTaskPriority,
  IWsMessageUpdateTaskStage,
  IWsMessageUpdateTaskSubtaskAssignee,
  IWsMessageUpdateTaskSubtaskStatus,
  IWsMessageUpdateTaskSubtaskTitle,
  IWsMessageUpdateTaskTag,
  IWsMessageUpdateTaskTextBlock,
  IWsMessageUpdateTaskTitle,
  IWsMessageCreateProjectRole,
  IWsMessageUpdateProjectRole,
  IWsMessageDeleteProjectRole,
  IWsMessageCreateProject,
  IWsMessageBlockedSubtask,
  IWsMessageUnblockedSubtask,
  IWsMessageCreateTaskFile,
  IWsMessageDeleteTaskFile,
  IWsMessageTaskBlocksSorted,
  IWsMessageCreateTaskTextBlocks,
  IWsMessageLibrary,
  IWsMessageUpdateVideoTranscodingProgress,
  IWsMessageChangeUploadTitle,
} from './websocket.interface'
import { Store } from '@ngrx/store'
import {
  addTaskFileWsStore,
  addTaskSubscriberStore,
  addTaskTagStore,
  blockSubtask,
  bulkCreateTaskBlocksStore,
  bulkCreateTaskBlocksWS,
  changeTaskSubtaskGroupStore,
  createTaskStore,
  createTaskSubtaskGroupSuccess,
  createTaskSubtaskSuccess,
  createTextBlockStore,
  deleteTaskStore,
  deleteTaskSubtaskGroupSuccess,
  deleteTaskSubtaskSuccess,
  deleteTextBlockSuccess,
  fileDeleteStore,
  removeTaskSubscriberStore,
  removeTaskTagStore,
  renameTaskSubtaskGroupStore,
  replaceTaskSubscriberStore,
  setTaskTagColorStore,
  sortTextBlocksStore,
  unblockSubtask,
  updateTaskDurationStore,
  updateTaskOwnerStore,
  updateTaskPointStore,
  updateTaskPriorityStore,
  updateTaskStageStore,
  updateTaskSubtaskAssigneeStore,
  updateTaskSubtaskStatusStore,
  updateTaskSubtaskTitleStore,
  updateTaskTitleStore,
  updateTextBlockStore,
} from '@app/tasks-module/state/tasks.actions'
import { StreamThreadActionsService } from '@app/stream-module/core/services/stream-thread-actions-service'
import { addProject, deleteProject, updateProjectStore } from '@app/projects-module/state/projects/projects.actions'
import {
  deleteFavoriteProjectStore,
  updateFavoriteProjectStore,
} from '@app/projects-module/state/favorite-projects/favorite-projects.actions'
import { CurrentProject } from '@shared/services/current-project'
import { CurrentOrganization } from '@shared/services/current-organization'
import { NavigateService } from '@shared/services/navigate'
import {
  addProjectRoleStore,
  removeProjectRoleStore,
  updateProjectRoleStore,
} from '@app/project-roles/state/project-roles.actions'
import { CommandGuidService } from '@shared/services/websocket/command-guid.service'
import { createGuid } from '@shared/helpers/guid-generator.helper'
import { ITextBlock } from '@app/tasks-module/core/data-types/text-block.interface'
import { ITaskBlocksSortingItem } from '@app/tasks-module/core/data-types/task.interface'
import { combineLatest, take } from 'rxjs'
import { ProjectsStoreFacade } from '@app/projects-module/state/projects/projects-store.facade'
import { UsersStoreFacade } from '@app/users-module/state/users-store.facade'
import { TasksService } from '@app/tasks-module/tasks.service'
import { GetTaskTagsQuery } from '@app/tasks-module/core/queries/get-task-tags.query'
import { VideoTranscodingService } from '@shared/services/video-transcoding'
import { LibraryFileService } from '@app/library-module/core/services/library-file.service'

@Injectable()
export class WebsocketCommandsService {
  constructor(
    private store: Store,
    private streamThreadActionsService: StreamThreadActionsService,
    private currentProject: CurrentProject,
    private currentOrganization: CurrentOrganization,
    private navigateService: NavigateService,
    private projectStoreFacade: ProjectsStoreFacade,
    private usersStoreFacade: UsersStoreFacade,
    private tasksService: TasksService,
    private commandGuidService: CommandGuidService,
    private getTaskTagsQuery: GetTaskTagsQuery,
    private videoTranscodingService: VideoTranscodingService,
    private libraryFileService: LibraryFileService
  ) {}

  replaceTaskSubscriber(message: IWsMessage<IWsMessageReplaceTaskSubscriber>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      userId: message.data.old_subscribed_user_id,
      replaceUserId: message.data.user_id,
    }
    this.store.dispatch(replaceTaskSubscriberStore(data))
  }

  addTaskSubscriber(message: IWsMessage<IWsMessageAddTaskSubscriber>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      userId: message.data.user_id,
    }
    this.store.dispatch(addTaskSubscriberStore(data))
  }

  deleteTaskSubscriber(message: IWsMessage<IWsMessageDeleteTaskSubscriber>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      userId: message.data.user_id,
    }
    this.store.dispatch(removeTaskSubscriberStore(data))
  }

  updateTaskTag(message: IWsMessage<IWsMessageUpdateTaskTag>): void {
    const data = {
      projectId: message.metadata.project_id,
      tagName: message.data.task_tag.name,
      newColorNumber: message.data.task_tag.color,
      prevColorNumber: message.data.task_tag.color,
    }
    this.store.dispatch(setTaskTagColorStore(data))
  }

  addTaskTag(message: IWsMessage<IWsMessageAddTaskTag>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      tag: {
        name: message.data.tag,
        color: message.data.color,
        id: message.data.id,
      },
    }
    this.store.dispatch(addTaskTagStore(data))
  }

  deleteTaskTag(message: IWsMessage<IWsMessageDeleteTaskTag>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      tagName: message.data.tag,
      tagId: message.data.id,
    }
    this.store.dispatch(removeTaskTagStore(data))
  }

  updateTaskStage(message: IWsMessage<IWsMessageUpdateTaskStage>): void {
    const newStage = {
      status_id: message.data.status_id,
      activated_at: message.data.activated_at,
      activated_by_id: message.data.activated_by_id,
    }

    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      statusId: message.data.status_id,
      prevStatusId: message.data.status_id,
      newStage: newStage,
      prevNewStage: newStage,
    }
    this.store.dispatch(updateTaskStageStore(data))
  }

  updateTaskPriority(message: IWsMessage<IWsMessageUpdateTaskPriority>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      priority: message.data.priority,
      prevPriority: message.data.priority,
    }
    this.store.dispatch(updateTaskPriorityStore(data))
  }

  updateTaskPoint(message: IWsMessage<IWsMessageUpdateTaskPoint>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      point: message.data.point_estimate,
      prevPoint: message.data.point_estimate,
    }
    this.store.dispatch(updateTaskPointStore(data))
  }

  updateTaskDuration(message: IWsMessage<IWsMessageUpdateTaskDuration>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      period: [message.data.start_at, message.data.end_at],
      prevPeriod: [message.data.start_at, message.data.end_at],
    }
    this.store.dispatch(updateTaskDurationStore(data))
  }

  updateTaskTitle(message: IWsMessage<IWsMessageUpdateTaskTitle>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      title: message.data.title,
      prevTitle: message.data.title,
    }
    this.store.dispatch(updateTaskTitleStore(data))
  }

  updateTaskOwner(message: IWsMessage<IWsMessageUpdateTaskOwner>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      ownerId: message.data.owner_id,
      prevOwnerId: message.data.owner_id,
    }
    this.store.dispatch(updateTaskOwnerStore(data))
  }

  updateTaskSubtaskAssignee(message: IWsMessage<IWsMessageUpdateTaskSubtaskAssignee>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask_id,
      newAssigneeId: message.data.assigned_user_id,
      prevAssigneeId: message.data.assigned_user_id,
      activatedById: message.data.activated_by_id,
    }
    this.store.dispatch(updateTaskSubtaskAssigneeStore(data))
  }

  updateTaskSubtaskTitle(message: IWsMessage<IWsMessageUpdateTaskSubtaskTitle>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask_id,
      newTitle: message.data.title,
      prevTitle: message.data.title,
    }
    this.store.dispatch(updateTaskSubtaskTitleStore(data))
  }

  updateTaskSubtaskStatus(message: IWsMessage<IWsMessageUpdateTaskSubtaskStatus>): void {
    const statusData = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask_id,
      updatedById: message.metadata.triggered_by_id,
      newStatusId: message.data.subtask_status_id,
      prevStatusId: message.data.subtask_status_id,
    }
    const assigneeData = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask_id,
      newAssigneeId: message.data.assigned_user_id,
      prevAssigneeId: message.data.assigned_user_id,
      activatedById: message.metadata.triggered_by_id,
    }
    this.store.dispatch(updateTaskSubtaskStatusStore(statusData))
    this.store.dispatch(updateTaskSubtaskAssigneeStore(assigneeData))
  }

  changeGroupTaskSubtask(message: IWsMessage<IWsMessageChangeGroupTaskSubtask>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      newGroupId: message.data.subtask_group_id,
      prevGroupId: message.data.old_group_id,
      subtaskId: message.data.subtask_id,
      newSubtaskIds: message.data.new_group_subtask_ids,
      prevSubtaskIds: message.data.new_group_subtask_ids,
    }
    this.store.dispatch(changeTaskSubtaskGroupStore(data))
  }

  createTaskSubtask(message: IWsMessage<IWsMessageCreateTaskSubtask>): void {
    // TODO: Удалить после фикса бекендда
    if (!message.data.subtask) {
      return
    }

    const data = {
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask.id,
      subtaskTitle: message.data.subtask.title,
      createdById: message.data.subtask.created_by_id,
      subtaskStatusId: message.data.subtask.subtask_status_id,
      guid: message.metadata.command_guid || undefined,
      assignedUserId: message.data.subtask.assigned_user_id || undefined,
    }
    this.store.dispatch(createTaskSubtaskSuccess(data))
  }

  deleteTaskSubtask(message: IWsMessage<IWsMessageDeleteTaskSubtask>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask_id,
    }
    this.store.dispatch(deleteTaskSubtaskSuccess(data))
  }

  createTaskSubtaskGroup(message: IWsMessage<IWsMessageCreateTaskSubtaskGroup>): void {
    const data = {
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group.id,
      subtaskGroupName: message.data.subtask_group.name,
      guid: message.metadata.command_guid || undefined,
      task_block_sorting: message.data.task_block_sorting[0],
      position: { position: message.data.position },
    }
    this.store.dispatch(createTaskSubtaskGroupSuccess(data))
  }

  renameTaskSubtaskGroup(message: IWsMessage<IWsMessageRenameTaskSubtaskGroup>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      newName: message.data.new_name,
      prevName: message.data.old_name,
    }
    this.store.dispatch(renameTaskSubtaskGroupStore(data))
  }

  deleteTaskSubtaskGroup(message: IWsMessage<IWsMessageDeleteTaskSubtaskGroup>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
    }
    this.store.dispatch(deleteTaskSubtaskGroupSuccess(data))
  }

  createTaskTextBlock(message: IWsMessage<IWsMessageCreateTaskTextBlock>): void {
    if (this.commandGuidService.includes(message.metadata.command_guid)) {
      return
    }
    const data = {
      taskId: message.data.task_id,
      textBlock: {
        ...message.data.text_block,
        extras: message.data.text_block.metadata || {},
        guid: createGuid(),
      },
      position: { position: message.data.position },
      task_block_sorting: message.data.task_block_sorting[0],
    }
    this.store.dispatch(createTextBlockStore(data))
  }

  createTaskTextBlocks(message: IWsMessage<IWsMessageCreateTaskTextBlocks>): void {
    if (this.commandGuidService.includes(message.metadata.command_guid)) {
      return
    }
    const data = {
      taskId: message.data.task_id,
      projectId: message.metadata.project_id,
      textBlocks: message.data.text_blocks
        .map((t) => {
          return { ...t, extras: t.metadata || {}, guid: t.guid || createGuid() }
        })
        .reverse(),
      taskBlockSortings: message.data.task_block_sortings,
    }
    this.store.dispatch(bulkCreateTaskBlocksWS(data))
  }

  updateTaskTextBlock(message: IWsMessage<IWsMessageUpdateTaskTextBlock>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      body: message.data.text_block.body,
      id: message.data.text_block.id!,
      guid: message.metadata.command_guid,
      blockType: message.data.text_block.type,
    }
    this.store.dispatch(updateTextBlockStore(data))
  }

  deleteTaskTextBlock(message: IWsMessage<IWsMessageDeleteTaskTextBlock>): void {
    const data = {
      projectId: message.metadata.project_id,
      taskId: message.data.task_id,
      blockId: message.data.text_block_id,
    }
    this.store.dispatch(deleteTextBlockSuccess(data))
  }

  createTask(message: IWsMessage<IWsMessageCreateTask>): void {
    const metaTask = message.data.task
    const project$ = this.projectStoreFacade.getProjectById(metaTask.project_id).pipe(take(1))
    const users$ = this.usersStoreFacade.getUsers().pipe(take(1))
    const taskTags$ = this.getTaskTagsQuery.call(this.currentOrganization.id, metaTask.project_id)

    combineLatest([project$, users$, taskTags$])
      .pipe(take(1))
      .subscribe(([project, users, taskTags]) => {
        let task = this.tasksService.buildTaskData(metaTask, project, users)
        task = {
          ...task,
          task_files: this.tasksService.buildFiles(task.task_files),
          text_blocks: this.tasksService.buildTextBlocks(task.text_blocks),
          tags: this.tasksService.buildTaskTags(task.tags, taskTags.data),
          subtask_groups: task.subtask_groups.map((group) => ({
            ...group,
            subtasks: this.tasksService.buildSubtasks(group),
          })),
        }

        this.store.dispatch(createTaskStore({ task }))
      })
  }

  deleteTask(message: IWsMessage<IWsMessageDeleteTask>): void {
    const data = {
      projectId: message.metadata.project_id,
      deletedById: message.metadata.triggered_by_id,
      taskId: message.data.task_id,
    }

    this.store.dispatch(deleteTaskStore(data))
  }

  updateStream(message: IWsMessage<IWsMessageUpdateStream>): void {
    this.streamThreadActionsService.addActions(message.data.actions)
  }

  createProject(message: IWsMessage<IWsMessageCreateProject>): void {
    const data = {
      project: message.data.project,
    }
    this.store.dispatch(addProject(data))
  }

  updateProject(message: IWsMessage<IWsMessageUpdateProject>): void {
    const data = {
      project: message.data.project,
    }
    this.store.dispatch(updateProjectStore(data))
    this.store.dispatch(updateFavoriteProjectStore(data))
  }

  deleteProject(message: IWsMessage<IWsMessageDeleteProject>): void {
    const data = {
      projectId: message.data.project_id,
    }
    if (this.currentProject.id === data.projectId) {
      this.navigateService.toProjects(this.currentOrganization.id)
      this.store.dispatch(deleteFavoriteProjectStore(data))
    }
    this.store.dispatch(deleteProject(data))
  }

  createProjectRole(message: IWsMessage<IWsMessageCreateProjectRole>): void {
    const data = {
      projectRole: message.data.project_role,
    }
    this.store.dispatch(addProjectRoleStore(data))
  }

  updateProjectRole(message: IWsMessage<IWsMessageUpdateProjectRole>): void {
    const data = {
      projectRole: message.data.project_role,
    }
    this.store.dispatch(updateProjectRoleStore(data))
  }

  deleteProjectRole(message: IWsMessage<IWsMessageDeleteProjectRole>): void {
    const data = {
      roleId: message.data.project_role_id,
    }
    this.store.dispatch(removeProjectRoleStore(data))
  }

  blockTaskSubtask(message: IWsMessage<IWsMessageBlockedSubtask>): void {
    const data = {
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask_id,
      userId: message.metadata.triggered_by_id,
    }
    this.store.dispatch(blockSubtask(data))
  }

  unblockTaskSubtask(message: IWsMessage<IWsMessageUnblockedSubtask>): void {
    const data = {
      taskId: message.data.task_id,
      subtaskGroupId: message.data.subtask_group_id,
      subtaskId: message.data.subtask_id,
    }
    this.store.dispatch(unblockSubtask(data))
  }

  createTaskFile(message: IWsMessage<IWsMessageCreateTaskFile>): void {
    const data = {
      taskId: message.data.task_id,
      file: message.data.task_file,
      position: { position: message.data.position },
      projectId: message.metadata.project_id,
      sortingItem: message.data.task_block_sortings[0],
    }
    this.store.dispatch(addTaskFileWsStore(data))
  }

  deleteTaskFile(message: IWsMessage<IWsMessageDeleteTaskFile>): void {
    const data = {
      taskId: message.data.task_id,
      fileId: message.data.task_file,
    }
    this.store.dispatch(fileDeleteStore(data))
  }

  taskBlocksSorted(message: IWsMessage<IWsMessageTaskBlocksSorted>): void {
    const data = {
      sort: message.data.task_block_sortings,
      taskId: message.data.task_id,
      projectId: message.metadata.project_id,
    }
    this.store.dispatch(sortTextBlocksStore(data))
  }

  updateVideoTranscodingData(message: IWsMessageLibrary<IWsMessageUpdateVideoTranscodingProgress>): void {
    const data = {
      upload_uuid: message.metadata.upload_uuid,
      big_preview_url: message.data.big_preview_url,
      small_preview_url: message.data.small_preview_url,
      progress: message.data.progress,
      state: message.data.state,
      type: message.data.type,
    }
    this.videoTranscodingService.update(data)
  }

  changeUploadTitle(message: IWsMessageLibrary<IWsMessageChangeUploadTitle>): void {
    if (!this.currentOrganization.id) {
      return
    }

    this.libraryFileService.changeTitle(message.metadata.upload_uuid, message.data.upload_title).subscribe(() => {
      this.libraryFileService.updateData()
    })
  }
}
